"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { springConfig } from "@/lib/animations";

export interface TutorialSlide {
  title: string;
  description: string;
  visual?: React.ReactNode;
}

interface TutorialSheetProps {
  isOpen: boolean;
  onClose: () => void;
  slides: TutorialSlide[];
}

export function TutorialSheet({ isOpen, onClose, slides }: TutorialSheetProps) {
  const t = useTranslations("tutorial");
  const [currentSlide, setCurrentSlide] = useState(0);

  const safeIndex = Math.min(currentSlide, slides.length - 1);

  const handleNext = () => {
    if (safeIndex < slides.length - 1) {
      setCurrentSlide(safeIndex + 1);
    } else {
      handleClose();
    }
  };

  const handlePrev = () => {
    if (safeIndex > 0) {
      setCurrentSlide(safeIndex - 1);
    }
  };

  const handleClose = () => {
    setCurrentSlide(0);
    onClose();
  };

  if (slides.length === 0) return null;

  return (
    <Sheet open={isOpen} onOpenChange={(open: boolean) => !open && handleClose()}>
      <SheetContent className="max-h-dvh sm:max-h-[85dvh] min-h-[630px]">
        <DialogPrimitive.Title className="sr-only">{slides[safeIndex].title}</DialogPrimitive.Title>
        <DialogPrimitive.Description className="sr-only">
          {slides[safeIndex].description}
        </DialogPrimitive.Description>

        <div className="flex flex-1 flex-col min-h-0 px-3 pb-4 pt-4 sm:px-6 sm:pt-6">
          <div className="flex justify-end shrink-0">
            <DialogPrimitive.Close className="shrink-0 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring">
              <X className="size-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </div>

          <div className="flex flex-1 flex-col min-h-0">
            <div className="flex flex-1 min-h-0 overflow-auto">
              <AnimatePresence mode="wait">
                <motion.div
                  key={safeIndex}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={springConfig}
                  className="m-auto flex w-full flex-col gap-8"
                >
                  {slides[safeIndex].visual && <div>{slides[safeIndex].visual}</div>}

                  <div className="text-center space-y-4">
                    <h2 className="text-2xl font-semibold tracking-tight">
                      {slides[safeIndex].title}
                    </h2>
                    <p className="text-muted-foreground leading-relaxed px-2">
                      {slides[safeIndex].description}
                    </p>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {slides.length > 1 && (
              <div className="flex justify-center gap-1.5 pt-6 shrink-0">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`h-2 cursor-pointer rounded-full transition-all ${
                      index === safeIndex
                        ? "w-8 bg-primary"
                        : "w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50"
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            )}

            <div className="flex items-center justify-center mt-6 gap-3 shrink-0">
              {slides.length > 1 && (
                <Button
                  variant="outline"
                  onClick={handlePrev}
                  disabled={safeIndex === 0}
                  className="flex-1"
                >
                  <ChevronLeft className="w-4 h-4 me-1" />
                  {t("previous")}
                </Button>
              )}
              <Button onClick={handleNext} className="flex-1">
                {safeIndex === slides.length - 1 ? (
                  t("getStarted")
                ) : (
                  <>
                    {t("next")}
                    <ChevronRight className="w-4 h-4 ms-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
